import React, { useEffect, useReducer, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { FooterSection, FaqSection, BreadCrumb } from '../components';
import { StationHeader, StationDetails, StationTrainList } from './components';
import DataLoaderError from '../../errors/DataLoaderError';
import StationInfoContext from './StationInfoContext';
import { StationInfoData, EnvironmentVariables } from '../types';
import { getAppEnv } from '../../../config/env';
import AdWrapper from '../components/AdWrapper';
import { AD_PAGES } from '../../utils/adsUtils';
import { RECOMMEND_PATH } from '../components/RecommendTripPathList/utils/recommendTripPathListUtils';
import RecommendTripPathWrapper from '../components/RecommendTripPathList/RecommendTripPathList';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import SEARCH_WIDGET_UTILS from '../components/SearchWidget/utils/utils';
import HtmlHeader from '../components/HtmlHeader';
import { POKUS_KEYS, SEO_STATION_PAGE } from '../../utils/RailsSEOConstants';
import AppDownloadBanner from '../components/AppDownloadBanner';
import usePokusApi from '../../hooks/usePokusApi';
import { isMobile } from '../../utils/commonUtils';

const { Provider: StationInfoProvider } = StationInfoContext;
const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);

type PathParamsType = {
  stationId: string;
};

type StationInfoProps = RouteComponentProps<PathParamsType> & {
  data: StationInfoData;
};

export function StationInfo({ data }: StationInfoProps) {
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);
  const { onTrainClicked, onMountInitSearchWidgetWrapper } =
    SEARCH_WIDGET_UTILS.STATION_INFO_UTILS(searchWidgetDispatch);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    setIsMobileDevice(isMobile());
  }, []);

  const Appdownload_banner_V2 = usePokusApi(POKUS_KEYS.AppDownloadBannerV2, false);

  return (
    <StationInfoProvider
      value={{
        ...data,
        onTrainClicked: onTrainClicked
      }}
    >
      {Appdownload_banner_V2 && isMobileDevice && <AppDownloadBanner {...data.template} />}
      <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
        <SearchWidget
          topFix={true}
          templateId={data.templateId}
          searchButtonTitle={data.miscellaneous['SELECT DATE AND CHECK AVAILABILITY']}
          onMountInitSearchWidget={onMountInitSearchWidgetWrapper(data)}
          pageWiseContext={SearchWidgetContext}
          pageName={SEO_STATION_PAGE}
        />
      </SearchWidgetContext.Provider>
      <div className="container">
        <HtmlHeader data={data} containsAlternateHref={true} />
      </div>
      <div className="railSeoWrapper">
        <BreadCrumb breadcrumb={data.template.breadcrumb} />
        <main>
          <StationHeader stationInfo={data.stationInfo.obj} template={data.template} />
          <StationDetails
            stationInfo={data.stationInfo.obj}
            title={data.stationInfo.title}
            alternatePageUrl={data.alternatePageUrlInfo}
          />
          <div className="appendBottom20 makeFlex perfectCenter">
            <AdWrapper adsPageName={AD_PAGES.STATION_INFO_PAGE.adsPageName} />
          </div>
          {data.trainsStartingFromStation.list.length === 0 &&
          data.trainsPassingThroughStation.list.length === 0 &&
          data.trainsEndingAtStation.list.length === 0 ? (
            <p className="railSeoWrap__infoTable--title no_margn textCenter padding20">
              There are no trains passing through this station.
            </p>
          ) : (
            <>
              <StationTrainList
                title={data.trainsStartingFromStation.title}
                trains={data.trainsStartingFromStation.list}
              />
              <StationTrainList
                title={data.trainsPassingThroughStation.title}
                trains={data.trainsPassingThroughStation.list}
              />
              <StationTrainList title={data.trainsEndingAtStation.title} trains={data.trainsEndingAtStation.list} />
            </>
          )}
          <section style={{ flexWrap: 'wrap' }} className="railSeoWrap__stnDetails makeFlex">
            <RecommendTripPathWrapper recommendPaths={RECOMMEND_PATH.STATION_INFO_PAGE(data)} />
          </section>
          <FaqSection
            faq={data.faq.list}
            pageUrl={data.pageUrl}
            title={data.faq.title}
            bookNowString={data.miscellaneous['BOOK NOW']}
            pageName={SEO_STATION_PAGE}
          />
        </main>
      </div>
      <FooterSection template={data.template} />
    </StationInfoProvider>
  );
}

StationInfo.fetchData = async (_, match): Promise<StationInfoData> => {
  const {
    params: { stationId, templateId }
  } = match;
  const url = `${DATA_LOADER_URL}/api/getStationTemplateData/${templateId}/${stationId}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(`Unable to fetch train details of ${stationId}`, responseData.error);
  }
  responseData.templateId = templateId;
  return responseData;
};

export default StationInfo;
